/**
 * API Client Configuration
 * 
 * Centralized axios instance with authentication interceptors.
 * Handles token refresh and automatic retry of failed requests.
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getCachedLocalStorage } from '../../utils/storage';

const API_URL = process.env.REACT_APP_API_URL || '';

interface QueueItem {
  resolve: (value: string | null) => void;
  reject: (error: Error) => void;
}

// Track if we're currently refreshing to avoid multiple refresh attempts
let isRefreshing = false;
let failedQueue: QueueItem[] = [];

const processQueue = (error: Error | null, token: string | null = null): void => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};


// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getCachedLocalStorage('quazi_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration and refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { refreshToken, logout } = await import('../../features/auth/services/auth');
        const result = await refreshToken();

        if (result.success) {
          const newToken = getCachedLocalStorage('quazi_access_token');
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return apiClient(originalRequest);
        } else {
          processQueue(new Error('Token refresh failed'), null);
          await logout();
          sessionStorage.setItem('session_expired', 'true');
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        const { logout } = await import('../../features/auth/services/auth');
        processQueue(refreshError as Error, null);
        await logout();
        sessionStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
